/**
 * Admin Withdrawals Management
 * View, approve, and reject withdrawal requests
 */

import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';

interface Withdrawal {
  id: string;
  userId: string;
  amount: number;
  description: string;
  status: string;
  wallet: string;
  memo: string;
  telegramUserId: string;
  username: string;
  netUsdt: number;
  feeUsdt: number;
  createdAt: string;
}

type FilterStatus = 'all' | 'pending' | 'approved' | 'rejected';

export function AdminWithdrawals() {
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterStatus>('pending');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [txHashInput, setTxHashInput] = useState<Record<string, string>>({});
  const [rejectReason, setRejectReason] = useState<Record<string, string>>({});
  const [approveReason, setApproveReason] = useState<Record<string, string>>({});
  const [showRejectForm, setShowRejectForm] = useState<string | null>(null);
  const [showApproveForm, setShowApproveForm] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = (id: string, text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const fetchWithdrawals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const status = filter === 'all' ? undefined : filter;
      const res = await api.getAdminWithdrawals(status);
      setWithdrawals(res.data.withdrawals || []);
    } catch (err) {
      setError('Failed to load withdrawals');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchWithdrawals();
  }, [fetchWithdrawals]);

  const handleApprove = async (id: string) => {
    const reason = approveReason[id];
    if (!reason || reason.trim().length === 0) {
      setError('Please enter an approval reason');
      return;
    }
    setActionLoading(id);
    try {
      await api.approveWithdrawal(id, txHashInput[id] || undefined, reason);
      setShowApproveForm(null);
      await fetchWithdrawals();
    } catch (err) {
      console.error(err);
      setError('Failed to approve withdrawal');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id: string) => {
    const reason = rejectReason[id];
    if (!reason || reason.trim().length === 0) {
      setError('Please enter a rejection reason');
      return;
    }
    setActionLoading(id);
    try {
      await api.rejectWithdrawal(id, reason);
      setShowRejectForm(null);
      await fetchWithdrawals();
    } catch (err) {
      console.error(err);
      setError('Failed to reject withdrawal');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return '#f59e0b';
      case 'approved': return '#10b981';
      case 'rejected': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusEmoji = (status: string) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'approved': return '✅';
      case 'rejected': return '❌';
      default: return '❓';
    }
  };

  const pendingCount = withdrawals.filter(w => w.status === 'pending').length;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)',
      color: '#fff',
      padding: '16px',
    }}>
      {/* Filter tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '16px',
        overflowX: 'auto',
      }}>
        {(['pending', 'all', 'approved', 'rejected'] as FilterStatus[]).map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            style={{
              padding: '8px 16px',
              borderRadius: '20px',
              border: filter === s ? '1px solid #3b82f6' : '1px solid rgba(255,255,255,0.1)',
              background: filter === s ? 'rgba(59,130,246,0.3)' : 'rgba(255,255,255,0.05)',
              color: filter === s ? '#60a5fa' : '#9ca3af',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {s === 'pending' ? `⏳ Pending${pendingCount > 0 ? ` (${pendingCount})` : ''}` :
             s === 'all' ? '📋 All' :
             s === 'approved' ? '✅ Approved' : '❌ Rejected'}
          </button>
        ))}
      </div>

      {error && (
        <div style={{
          background: 'rgba(239,68,68,0.15)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '16px',
          color: '#fca5a5',
          fontSize: '13px',
        }}>
          {error}
          <button onClick={() => setError(null)} style={{
            float: 'right', background: 'none', border: 'none',
            color: '#fca5a5', cursor: 'pointer', fontSize: '16px',
          }}>×</button>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          Loading...
        </div>
      ) : withdrawals.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
          <div>No {filter === 'all' ? '' : filter} withdrawals</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {withdrawals.map(w => (
            <div key={w.id} style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '12px',
              padding: '16px',
            }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '20px' }}>💸</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '16px' }}>
                      {Math.abs(w.amount).toFixed(2)} USDT
                    </div>
                    <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                      Net: {w.netUsdt?.toFixed(2)} USDT · Fee: {w.feeUsdt?.toFixed(2)} USDT
                    </div>
                  </div>
                </div>
                <span style={{
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  background: `${getStatusColor(w.status)}20`,
                  color: getStatusColor(w.status),
                  border: `1px solid ${getStatusColor(w.status)}40`,
                }}>
                  {getStatusEmoji(w.status)} {w.status}
                </span>
              </div>

              {/* User info */}
              <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '8px' }}>
                <div>👤 {w.username || 'N/A'} (ID: {w.telegramUserId})</div>
                <div style={{ marginTop: '4px', wordBreak: 'break-all', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                  <span>📬 Wallet:</span>
                  <span
                    onClick={() => handleCopy(`wallet-${w.id}`, w.wallet)}
                    style={{ color: '#60a5fa', cursor: 'pointer', textDecoration: 'underline', flex: 1 }}
                  >
                    {w.wallet}
                  </span>
                  <button
                    onClick={() => handleCopy(`wallet-${w.id}`, w.wallet)}
                    style={{
                      background: copiedId === `wallet-${w.id}` ? 'rgba(16,185,129,0.2)' : 'rgba(96,165,250,0.15)',
                      border: copiedId === `wallet-${w.id}` ? '1px solid rgba(16,185,129,0.4)' : '1px solid rgba(96,165,250,0.3)',
                      borderRadius: '6px',
                      padding: '2px 8px',
                      color: copiedId === `wallet-${w.id}` ? '#6ee7b7' : '#60a5fa',
                      fontSize: '11px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    {copiedId === `wallet-${w.id}` ? '✓' : '📋'}
                  </button>
                </div>
                {w.memo && (
                  <div style={{ marginTop: '4px', wordBreak: 'break-all', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                    <span>📝 Memo:</span>
                    <span
                      onClick={() => handleCopy(`memo-${w.id}`, w.memo)}
                      style={{ color: '#f59e0b', cursor: 'pointer', textDecoration: 'underline', flex: 1 }}
                    >
                      {w.memo}
                    </span>
                    <button
                      onClick={() => handleCopy(`memo-${w.id}`, w.memo)}
                      style={{
                        background: copiedId === `memo-${w.id}` ? 'rgba(16,185,129,0.2)' : 'rgba(245,158,11,0.15)',
                        border: copiedId === `memo-${w.id}` ? '1px solid rgba(16,185,129,0.4)' : '1px solid rgba(245,158,11,0.3)',
                        borderRadius: '6px',
                        padding: '2px 8px',
                        color: copiedId === `memo-${w.id}` ? '#6ee7b7' : '#f59e0b',
                        fontSize: '11px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                    >
                      {copiedId === `memo-${w.id}` ? '✓' : '📋'}
                    </button>
                  </div>
                )}
                <div style={{ marginTop: '4px' }}>
                  🕐 {new Date(w.createdAt).toLocaleString()}
                </div>
              </div>

              {/* Actions for pending */}
              {w.status === 'pending' && (
                <div style={{ marginTop: '12px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '12px' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => setShowApproveForm(showApproveForm === w.id ? null : w.id)}
                      disabled={actionLoading === w.id}
                      style={{
                        flex: 1,
                        padding: '10px',
                        borderRadius: '8px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #059669, #10b981)',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '14px',
                        cursor: actionLoading === w.id ? 'not-allowed' : 'pointer',
                        opacity: actionLoading === w.id ? 0.6 : 1,
                      }}
                    >
                      ✅ Approve
                    </button>
                    <button
                      onClick={() => setShowRejectForm(showRejectForm === w.id ? null : w.id)}
                      disabled={actionLoading === w.id}
                      style={{
                        flex: 1,
                        padding: '10px',
                        borderRadius: '8px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #dc2626, #ef4444)',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '14px',
                        cursor: actionLoading === w.id ? 'not-allowed' : 'pointer',
                        opacity: actionLoading === w.id ? 0.6 : 1,
                      }}
                    >
                      ❌ Reject
                    </button>
                  </div>

                  {/* Approve reason form */}
                  {showApproveForm === w.id && (
                    <div style={{ marginTop: '8px' }}>
                      <input
                        type="text"
                        placeholder="TX Hash (optional)"
                        value={txHashInput[w.id] || ''}
                        onChange={e => setTxHashInput(prev => ({ ...prev, [w.id]: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '1px solid rgba(16,185,129,0.3)',
                          background: 'rgba(16,185,129,0.1)',
                          color: '#fff',
                          fontSize: '13px',
                          marginBottom: '8px',
                          boxSizing: 'border-box',
                        }}
                      />
                      <input
                        type="text"
                        placeholder="Approval reason (required)"
                        value={approveReason[w.id] || ''}
                        onChange={e => setApproveReason(prev => ({ ...prev, [w.id]: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '1px solid rgba(16,185,129,0.3)',
                          background: 'rgba(16,185,129,0.1)',
                          color: '#fff',
                          fontSize: '13px',
                          marginBottom: '8px',
                          boxSizing: 'border-box',
                        }}
                      />
                      <button
                        onClick={() => handleApprove(w.id)}
                        disabled={actionLoading === w.id}
                        style={{
                          width: '100%',
                          padding: '10px',
                          borderRadius: '8px',
                          border: '1px solid rgba(16,185,129,0.3)',
                          background: 'rgba(16,185,129,0.2)',
                          color: '#6ee7b7',
                          fontWeight: 600,
                          fontSize: '13px',
                          cursor: actionLoading === w.id ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {actionLoading === w.id ? 'Processing...' : 'Confirm Approve'}
                      </button>
                    </div>
                  )}

                  {/* Reject reason form */}
                  {showRejectForm === w.id && (
                    <div style={{ marginTop: '8px' }}>
                      <input
                        type="text"
                        placeholder="Rejection reason (required)"
                        value={rejectReason[w.id] || ''}
                        onChange={e => setRejectReason(prev => ({ ...prev, [w.id]: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '1px solid rgba(239,68,68,0.3)',
                          background: 'rgba(239,68,68,0.1)',
                          color: '#fff',
                          fontSize: '13px',
                          marginBottom: '8px',
                          boxSizing: 'border-box',
                        }}
                      />
                      <button
                        onClick={() => handleReject(w.id)}
                        disabled={actionLoading === w.id}
                        style={{
                          width: '100%',
                          padding: '10px',
                          borderRadius: '8px',
                          border: '1px solid rgba(239,68,68,0.3)',
                          background: 'rgba(239,68,68,0.2)',
                          color: '#fca5a5',
                          fontWeight: 600,
                          fontSize: '13px',
                          cursor: actionLoading === w.id ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {actionLoading === w.id ? 'Processing...' : 'Confirm Reject & Refund'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
